import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useChat } from '../contexts/ChatContext'
import { anunciosService, fretesService, favoritosService, getFotoUrl } from '../services/api'
import Selo from '../components/Selo'
import PlanosModal from '../components/PlanosModal'
import CheckoutModal from '../components/CheckoutModal'
import './AnuncioDetalhe.css'

// Mock de dados do anúncio (Fallback)
const anunciosMock = {
    1: {
        id: 1,
        titulo: 'Lote de Nelore - 50 cabeças',
        descricao: 'Lote excepcional de gado Nelore criado em pasto rotacionado. Animais saudáveis, vacinados e com documentação em dia. Excelente para engorda ou reprodução. Peso médio de 450kg por cabeça.',
        preco: 250000,
        quantidade: 50,
        peso: '450kg média',
        idade: '24 meses',
        raca: 'Nelore',
        categoria: 'Bovino',
        localizacao: 'Uberaba, MG',
        selo: 'ouro',
        vendedor: {
            nome: 'Fazenda São João',
            verificado: true,
            desde: '2024',
            anuncios: 15,
            avaliacao: 4.8
        },
        imagens: [
            'https://images.unsplash.com/photo-1570042225831-d98fa7577f1e?w=800&h=500&fit=crop',
            'https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=800&h=500&fit=crop',
            'https://images.unsplash.com/photo-1527153857715-3908f2bae5e8?w=800&h=500&fit=crop'
        ],
        criadoEm: '2026-02-01'
    }
}

function AnuncioDetalhe() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { usuario, isLogado } = useAuth()
    const { openChat } = useChat()

    const [anuncio, setAnuncio] = useState(null)
    const [loading, setLoading] = useState(true)
    const [erro, setErro] = useState(null)

    // Estado para candidatura de frete
    const [modalFreteAberto, setModalFreteAberto] = useState(false)
    const [precoFrete, setPrecoFrete] = useState('')
    const [mensagemFrete, setMensagemFrete] = useState('')
    const [enviandoCandidatura, setEnviandoCandidatura] = useState(false)
    const [showSucessoModal, setShowSucessoModal] = useState(false)
    const [imagemAtual, setImagemAtual] = useState(0)

    // Estado de Favorito
    const [isFavorito, setIsFavorito] = useState(false)
    const [loadingFavorito, setLoadingFavorito] = useState(false)

    // Estados para monetização motorista
    const [showPlanosModal, setShowPlanosModal] = useState(false)
    const [checkoutPlano, setCheckoutPlano] = useState(null)

    useEffect(() => {
        carregarAnuncio()
    }, [id])

    const [listaCandidaturas, setListaCandidaturas] = useState([])

    useEffect(() => {
        if (isLogado && id) {
            checarFavorito()
        }
    }, [isLogado, id])

    useEffect(() => {
        if (isLogado && id && anuncio && usuario && anuncio.vendedor?.id === usuario.id) {
            buscarCandidaturasFrete()
        }
    }, [isLogado, id, anuncio, usuario])

    const buscarCandidaturasFrete = async () => {
        try {
            const response = await fretesService.candidaturasPorAnuncio(id)
            setListaCandidaturas(response.candidaturas || [])
        } catch (error) {
            console.error('Erro ao buscar candidaturas de frete:', error)
        }
    }

    const checarFavorito = async () => {
        try {
            const response = await favoritosService.checarStatus(id)
            setIsFavorito(response.favoritado)
        } catch (error) {
            console.error('Erro ao checar favorito:', error)
        }
    }

    const handleToggleFavorito = async () => {
        if (!isLogado) {
            navigate('/login')
            return
        }

        setLoadingFavorito(true)
        try {
            const response = await favoritosService.toggle(id)
            setIsFavorito(response.favoritado)
        } catch (error) {
            console.error('Erro ao favoritar:', error)
            alert('Erro ao atualizar favoritos')
        } finally {
            setLoadingFavorito(false)
        }
    }

    const carregarAnuncio = async () => {
        setLoading(true)
        try {
            // Tentar buscar da API
            const response = await anunciosService.buscarPorId(id)
            setAnuncio(response.anuncio)
        } catch (error) {
            console.error("Erro ao buscar anúncio da API:", error)
            // Fallback para mock se for ID numérico (ex: 1)
            if (anunciosMock[id]) {
                setAnuncio(anunciosMock[id])
            } else {
                setErro("Anúncio não encontrado.")
            }
        } finally {
            setLoading(false)
        }
    }

    // Verificar se pode ver detalhes
    const podeVerDetalhes = isLogado && usuario?.perfilCompleto

    // Verificar se é motorista para mostrar botão de frete
    const ehMotorista = isLogado && 
        usuario?.motoristaVerificado === true && 
        usuario?.expiracao_motorista && 
        new Date(usuario.expiracao_motorista) > new Date()

    const handleCandidatarClick = () => {
        if (!usuario?.motoristaVerificado) {
            setShowPlanosModal(true)
            return
        }
        setModalFreteAberto(true)
    }

    const handleCandidatarFrete = async (e) => {
        e.preventDefault()
        if (!precoFrete) return

        setEnviandoCandidatura(true)
        try {
            await fretesService.candidatar({
                anuncioId: anuncio.id,
                precoPoposto: parseFloat(precoFrete.replace('R$', '').replace('.', '').replace(',', '.')),
                mensagem: mensagemFrete
            })
            setShowSucessoModal(true)
            setModalFreteAberto(false)
            setPrecoFrete('')
            setMensagemFrete('')
        } catch (error) {
            console.error(error)
            alert(error.message || 'Erro ao enviar candidatura')
        } finally {
            setEnviandoCandidatura(false)
        }
    }

    const handlePlanoSelecionado = async (plano) => {
        try {
            const token = localStorage.getItem('token');
            const resp = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/pagamentos/checkout-anuncio`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ plano })
            });
            const data = await resp.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                alert('Erro ao gerar pagamento');
            }
        } catch (e) {
            console.error(e);
            alert('Falha na comunicação com gateway de pagamento');
        }
    };

    const handleTenhoInteresse = () => {
        if (!isLogado) {
            navigate('/login')
            return
        }

        if (usuario.id === anuncio.vendedor.id) {
            alert('Você é o dono deste anúncio!')
            return
        }

        openChat(anuncio.vendedor, `Olá! Tenho interesse no seu anúncio "${anuncio.titulo}". Está disponível?`)
    }

    const formatPrice = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0
        }).format(value)
    }

    if (loading) return <div className="loading">Carregando anúncio...</div>
    if (erro || !anuncio) return <div className="erro">{erro || "Anúncio não encontrado"}</div>



    const precoUnitario = anuncio.preco / anuncio.quantidade

    // Se não está logado ou perfil incompleto, mostrar bloqueio
    if (!podeVerDetalhes) {
        return (
            <div className="anuncio-detalhe">
                <Link to="/anuncios" className="voltar-link">
                    ← Voltar para anúncios
                </Link>

                <div className="detalhe-grid">
                    {/* Preview bloqueado */}
                    <div className="galeria">
                        <div className="galeria-principal bloqueado">
                            <img
                                src={anuncio.imagens?.[0] || anuncio.imagem}
                                alt={anuncio.titulo}
                                style={{ filter: 'blur(8px)' }}
                            />
                            <div className="bloqueio-overlay">
                                <div className="bloqueio-icon">🔒</div>
                                <h3>Conteúdo Bloqueado</h3>
                                <p>Complete seu cadastro para ver os detalhes deste anúncio</p>
                            </div>
                        </div>
                    </div>

                    {/* Info bloqueada */}
                    <div className="info-container">
                        <div className="info-header">
                            <h1>{anuncio.titulo}</h1>
                            <div className="info-meta">
                                <span className="badge badge-info">{anuncio.raca}</span>
                                <span className="badge badge-success">{anuncio.categoria}</span>
                                <span>📍 {anuncio.localizacao || `${anuncio.cidade}, ${anuncio.estado}`}</span>
                            </div>
                        </div>

                        <div className="bloqueio-card">
                            <div className="bloqueio-card-icon">🔐</div>
                            <h2>Complete seu cadastro para ver mais!</h2>
                            <p>
                                Para visualizar preços, detalhes do anúncio e entrar em contato com o vendedor,
                                você precisa completar seu cadastro.
                            </p>
                            <ul className="bloqueio-beneficios">
                                <li>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" width="20">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Ver preços e detalhes completos
                                </li>
                                <li>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" width="20">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Entrar em contato com vendedores
                                </li>
                                <li>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" width="20">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Solicitar fretes
                                </li>
                                <li>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" width="20">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Receber ofertas exclusivas
                                </li>
                            </ul>

                            {isLogado ? (
                                <button
                                    className="btn btn-primary btn-lg btn-block"
                                    onClick={() => navigate('/completar-perfil')}
                                    style={{ borderRadius: '12px', height: '56px', fontWeight: '800' }}
                                >
                                    ✓ Completar meu Cadastro
                                </button>
                            ) : (
                                <div className="bloqueio-actions">
                                    <button
                                        className="btn btn-primary btn-lg"
                                        onClick={() => navigate('/cadastro')}
                                        style={{ borderRadius: '12px', height: '56px', fontWeight: '800', boxShadow: '0 10px 20px -5px rgba(34, 197, 94, 0.4)' }}
                                    >
                                        Criar Conta Grátis
                                    </button>
                                    <button
                                        className="btn btn-secondary btn-lg"
                                        onClick={() => navigate('/login')}
                                        style={{ borderRadius: '12px', height: '56px', fontWeight: '800', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                                    >
                                        Já tenho conta
                                    </button>
                                </div>
                            )}

                            <p className="bloqueio-tempo">
                                ⏱️ Leva menos de 2 minutos!
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="anuncio-detalhe">
            <Link to="/anuncios" className="voltar-link">
                ← Voltar para anúncios
            </Link>

            <div className="detalhe-grid">
                {/* Galeria */}
                {/* Galeria */}
                <div className="galeria">
                    <div className="galeria-principal" style={{ backgroundColor: '#000' }}>
                        {(() => {
                            const rawUrl = anuncio.midias?.length > 0 
                                ? anuncio.midias[imagemAtual].url_original 
                                : (anuncio.imagens?.[imagemAtual] || anuncio.imagem);
                            
                            const tipo = anuncio.midias?.length > 0 ? anuncio.midias[imagemAtual].tipo : 'foto';

                            if (!rawUrl) return <img src="https://via.placeholder.com/800x500?text=Sem+Foto" alt="Sem foto" />;

                            const isVideo = tipo === 'video' || rawUrl.match(/\.(mp4|mov|avi|webm)$/i) || rawUrl.includes('/video/upload/');

                            if (isVideo) {
                                const videoUrl = rawUrl.startsWith('http') ? rawUrl : `${import.meta.env.VITE_API_URL || 'https://api.agroov.com'}${rawUrl}`;
                                return (
                                    <video 
                                        src={videoUrl} 
                                        controls 
                                        autoPlay={imagemAtual === 0}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                );
                            }

                            return (
                                <img
                                    src={getFotoUrl(rawUrl)}
                                    alt={`${anuncio.titulo} - Foto ${imagemAtual + 1}`}
                                />
                            );
                        })()}
                        {/* Navegação da Galeria */}
                        {((anuncio.midias?.length || anuncio.imagens?.length) > 1) && (
                            <>
                                <button
                                    className="galeria-nav prev"
                                    onClick={() => setImagemAtual(prev => prev === 0 ? (anuncio.midias?.length || anuncio.imagens?.length) - 1 : prev - 1)}
                                >
                                    ❮
                                </button>
                                <button
                                    className="galeria-nav next"
                                    onClick={() => setImagemAtual(prev => prev === (anuncio.midias?.length || anuncio.imagens?.length) - 1 ? 0 : prev + 1)}
                                >
                                    ❯
                                </button>
                            </>
                        )}
                    </div>
                    <div className="galeria-thumbs">
                        {(anuncio.midias || anuncio.imagens)?.map((img, i) => {
                            const thumbUrl = img.url_original || img;
                            const isVideo = (img.tipo === 'video') || thumbUrl.match(/\.(mp4|mov|avi|webm)$/i) || thumbUrl.includes('/video/upload/');
                            return (
                                <div 
                                    key={i} 
                                    className={`thumb-container ${i === imagemAtual ? 'active' : ''}`}
                                    onClick={() => setImagemAtual(i)}
                                    style={{ position: 'relative', cursor: 'pointer' }}
                                >
                                    <img
                                        src={getFotoUrl(thumbUrl)}
                                        alt={`${anuncio.titulo} miniatura ${i + 1}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    {isVideo && (
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <span style={{ color: 'white', fontSize: '14px', marginLeft: '2px' }}>▶</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Info */}
                <div className="info-container">
                    <div className="info-header">
                        <h1>{anuncio.titulo}</h1>
                        <div className="info-meta">
                            {anuncio.categoria !== 'Outros' && anuncio.raca && (
                                <span className="badge badge-info">{anuncio.raca}</span>
                            )}
                            <span className="badge badge-success">{anuncio.categoria}</span>
                            <span>Localização: {anuncio.localizacao || `${anuncio.cidade} - ${anuncio.estado}`}</span>
                        </div>
                    </div>

                    <div className="info-precos">
                        <div className="preco-total">
                            <span className="preco-label">Preço total</span>
                            <span className="price">{formatPrice(anuncio.preco)}</span>
                        </div>
                        <div className="preco-unitario">
                            <span className="preco-label">{anuncio.categoria === 'Outros' ? 'Por unidade' : 'Por cabeça'}</span>
                            <span className="preco-valor">{formatPrice(precoUnitario)}</span>
                        </div>
                    </div>

                    <div className="info-specs-card" style={{ background: 'var(--color-bg-card)', padding: 'var(--spacing-md) var(--spacing-lg)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                        <h4 style={{ color: 'var(--color-text-primary)', fontSize: '1rem', fontWeight: '600', margin: 0, borderBottom: '1px solid var(--color-border)', paddingBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Detalhes do Anúncio
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {anuncio.nomeFazenda && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                                    <span style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>Fazenda</span>
                                    <span style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                                        🏡 {anuncio.nomeFazenda}
                                    </span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                                <span style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>Quantidade</span>
                                <span style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                                    {anuncio.quantidade} {anuncio.categoria === 'Outros' ? 'unidades' : 'cabeças'}
                                </span>
                            </div>
                            
                            {anuncio.categoria !== 'Outros' && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                                        <span style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>Peso Médio</span>
                                        <span style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>{anuncio.peso || anuncio.pesoMedio} {anuncio.unidadePeso || 'kg'}</span>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>Idade</span>
                                        <span style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>{anuncio.idade || anuncio.idadeMeses} {anuncio.unidadeIdade || 'meses'}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="info-actions">
                        {/* Ações para Vendedor (Dono do Anúncio) */}
                        {usuario?.id === anuncio.vendedor.id ? (
                            <button
                                className="btn btn-secondary btn-lg btn-block"
                                onClick={() => navigate('/meus-anuncios')}
                            >
                                ⚙️ Gerenciar Meu Anúncio
                            </button>
                        ) : (
                            <div className="comprador-actions" style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', padding: '5px 0' }}>
                                {/* Botão de Frete - Exibido se for elegível */}
                                {ehMotorista && (
                                    <button
                                        className="btn pulse-btn"
                                        onClick={handleCandidatarClick}
                                        style={{ 
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 
                                            color: '#ffffff',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '12px',
                                            padding: '14px 20px',
                                            fontSize: '1rem',
                                            fontWeight: '700',
                                            letterSpacing: '0.5px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                            boxShadow: '0 10px 20px -5px rgba(59, 130, 246, 0.4)',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease'
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" style={{ width: '22px', height: '22px' }}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM18.75 18.75a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM3.75 6H16.5m0 0l3.75 3.75m-3.75-3.75v11.25m3.75-7.5v7.5m-16.5-3.75V6m16.5 3.75H3.75m1.5 0v6.75H15V9.75H5.25Z" />
                                        </svg>
                                        Quero fazer esse frete
                                    </button>
                                )}

                                <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                                    <button
                                        className="btn"
                                        style={{ 
                                            flex: 1,
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '12px',
                                            padding: '14px 20px',
                                            fontSize: '1rem',
                                            fontWeight: '700',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                            boxShadow: '0 10px 20px -5px rgba(16, 185, 129, 0.4)',
                                            cursor: 'pointer'
                                        }}
                                        onClick={handleTenhoInteresse}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" style={{ width: '22px', height: '22px' }}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
                                        </svg>
                                        Conversar no Chat
                                    </button>
                                    
                                    <button
                                        className="btn"
                                        onClick={handleToggleFavorito}
                                        disabled={loadingFavorito}
                                        style={{ 
                                            minWidth: '55px', 
                                            background: '#1e293b',
                                            color: isFavorito ? '#eab308' : '#94a3b8',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '12px',
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            fontSize: '1.3rem'
                                        }}
                                    >
                                        {isFavorito ? '★' : '☆'}
                                    </button>
                                </div>

                                {anuncio.vendedor?.whatsapp && (
                                    <a
                                        href={`https://wa.me/${anuncio.vendedor.whatsapp.replace(/\D/g, '').startsWith('55') ? anuncio.vendedor.whatsapp.replace(/\D/g, '') : '55' + anuncio.vendedor.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá! Vi seu anúncio "${anuncio.titulo}" no Agroov e tenho interesse.`)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ 
                                            background: 'rgba(16, 185, 129, 0.15)', 
                                            color: '#10b981', 
                                            border: '1px solid rgba(16, 185, 129, 0.3)',
                                            borderRadius: '12px',
                                            padding: '12px 20px',
                                            fontSize: '0.95rem',
                                            fontWeight: '700',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                            textDecoration: 'none',
                                            width: '100%',
                                            marginTop: '4px',
                                            transition: 'background 0.2s ease'
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" style={{ width: '18px', height: '18px' }}>
                                            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-5.5-2.8-23.2-8.5-44.2-27.1-16.4-14.6-27.4-32.7-30.6-38.2-3.2-5.6-.3-8.6 2.5-11.3 2.5-2.5 5.5-6.5 8.3-9.7 2.8-3.3 3.7-5.5 5.6-9.2 1.9-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 20.3 53.7 23.1 57.4 2.8 3.7 39.9 60.9 96.6 85.3 13.5 5.8 24 9.2 32.2 11.9 13.5 4.3 25.8 3.7 35.6 2.3 10.9-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                                        </svg>
                                        WhatsApp Direto
                                    </a>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Vendedor */}
                    <div className="vendedor-card">
                        <div className="vendedor-header">
                            <div className="vendedor-avatar" style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {anuncio.vendedor.foto ? (
                                    <img 
                                        src={getFotoUrl(anuncio.vendedor.foto)} 
                                        alt={anuncio.vendedor.nome} 
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                    />
                                ) : (
                                    anuncio.vendedor.nome.charAt(0).toUpperCase()
                                )}
                            </div>
                            <div className="vendedor-info">
                                <h3>
                                    {anuncio.vendedor.nome}
                                </h3>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px', marginBottom: '4px' }}>
                                    {anuncio.vendedor.assinaturaIlimitada === true ? (
                                        <span style={{ background: '#f59e0b', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            ⭐ Vendedor Premium
                                        </span>
                                    ) : (
                                        <span style={{ background: '#22c55e', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            🏷️ Vendedor
                                        </span>
                                    )}
                                </div>
                                <span className="vendedor-desde" style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>
                                    📍 {anuncio.vendedor.cidade || anuncio.cidade} - {anuncio.vendedor.estado || anuncio.estado}
                                </span>
                                <span className="vendedor-desde" style={{ display: 'block', fontWeight: '600', color: 'var(--color-text-primary)', fontSize: '0.85rem' }}>
                                    📞 {anuncio.vendedor.whatsapp || 'Não informado'}
                                </span>
                            </div>
                        </div>
                        <div className="vendedor-stats">
                            <div className="vendedor-stat">
                                <span className="stat-value">{anuncio.vendedor.anuncios || anuncio.vendedor.totalAnuncios || 0}</span>
                                <span className="stat-label">Anúncios</span>
                            </div>
                            <div className="vendedor-stat">
                                <span className="stat-value">🤝 {anuncio.vendedor.vendasRealizadas || 0}</span>
                                <span className="stat-label">Vendas</span>
                            </div>
                            <div className="vendedor-stat">
                                <span className="stat-value">⭐ {anuncio.vendedor.avaliacao || '5.0'}</span>
                                <span className="stat-label">Avaliação</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Descrição */}
            <div className="descricao-section">
                <h2>Descrição</h2>
                <p>{anuncio.descricao}</p>
            </div>

            {/* Candidaturas de Frete para o Vendedor */}
            {isLogado && anuncio && usuario && anuncio.vendedor?.id === usuario.id && (
                <div className="candidaturas-vendedor-section" style={{ marginTop: 'var(--spacing-xl)', background: 'var(--color-bg-card)', padding: 'var(--spacing-lg)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
                    <h2 style={{ color: 'var(--color-text-primary)', fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        🚚 Propostas de Frete Recebidas ({listaCandidaturas.length})
                    </h2>
                    
                    {listaCandidaturas.length === 0 ? (
                        <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem 0', fontSize: '0.95rem' }}>
                            Nenhuma candidatura de frete recebida para este lote até o momento.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {listaCandidaturas.map(cand => (
                                <div key={cand.id} style={{ 
                                    background: 'var(--color-bg-secondary)', 
                                    padding: '1.25rem', 
                                    borderRadius: 'var(--radius-md)', 
                                    border: '1px solid rgba(255,255,255,0.03)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    flexWrap: 'wrap'
                                }}>
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                            <strong style={{ color: 'var(--color-text-primary)', fontSize: '1rem' }}>{cand.motorista_nome}</strong>
                                            <span style={{ background: 'var(--color-primary-light)', color: 'var(--color-primary)', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                {cand.tipo_veiculo || 'Motorista'}
                                            </span>
                                        </div>
                                        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0 0 8px 0' }}>
                                            Localização: {cand.motorista_cidade || 'Não informada'}
                                        </p>
                                        {cand.mensagem && (
                                            <p style={{ color: 'var(--color-text-primary)', background: 'rgba(0,0,0,0.2)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: '0.9rem', margin: 0, borderLeft: '3px solid var(--color-primary)' }}>
                                                "{cand.mensagem}"
                                            </p>
                                        )}
                                    </div>

                                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                        <div style={{ fontSize: '1.2rem', fontWeight: '800', color: 'var(--color-success)' }}>
                                            {formatPrice(cand.preco_proposto || cand.precoProposto)}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button 
                                                className="btn btn-outline btn-sm"
                                                onClick={() => openChat({
                                                    id: cand.motorista_id || cand.motoristaId,
                                                    nome: cand.motorista_nome,
                                                    foto: cand.motorista_foto
                                                })}
                                            >
                                                💬 Conversar no Chat
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Modal de Candidatura de Frete */}
            {modalFreteAberto && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>🚚 Candidatar-se ao Frete</h2>
                            <button className="close-btn" onClick={() => setModalFreteAberto(false)}>×</button>
                        </div>
                        <form onSubmit={handleCandidatarFrete}>
                            <div className="form-group">
                                <label>Valor do Frete (R$)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={precoFrete}
                                    onChange={(e) => setPrecoFrete(e.target.value)}
                                    placeholder="Ex: 1500.00"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Mensagem para o Vendedor</label>
                                <textarea
                                    className="form-input"
                                    value={mensagemFrete}
                                    onChange={(e) => setMensagemFrete(e.target.value)}
                                    placeholder="Ex: Tenho caminhão boiadeiro disponível para buscar amanhã..."
                                    rows="4"
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setModalFreteAberto(false)}>Cancelar</button>
                                <button type="submit" className="btn btn-primary" disabled={enviandoCandidatura}>
                                    {enviandoCandidatura ? 'Enviando...' : 'Enviar Proposta'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Modais de Pagamento para Motorista */}
            {showPlanosModal && (
                <PlanosModal onClose={() => setShowPlanosModal(false)} onPlanoSelecionado={handlePlanoSelecionado} />
            )}

            {checkoutPlano && (
                <CheckoutModal
                    plano={checkoutPlano}
                    onClose={() => setCheckoutPlano(null)}
                    onConfirmado={() => window.location.reload()}
                />
            )}
            {showSucessoModal && (
                <div className="modal-backdrop">
                    <div className="sucesso-modal" style={{ maxWidth: '400px', margin: '0 auto' }}>
                        <div className="modal-icon-container">
                            <div className="modal-icon-check">✓</div>
                        </div>
                        <h2>Candidatura Enviada!</h2>
                        <p>A sua proposta de frete foi registrada e o vendedor será notificado.</p>
                        
                        <div className="modal-actions">
                            <button 
                                className="btn btn-primary" 
                                style={{ width: '100%', padding: '12px', borderRadius: '8px', fontWeight: 'bold' }}
                                onClick={() => {
                                    setShowSucessoModal(false);
                                    window.location.reload();
                                }}
                            >
                                📦 Ok, entendi!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AnuncioDetalhe
